import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Users, BookOpen, ListChecks, Target } from "lucide-react";

function fmtTime(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
    })();
  }, []);

  const kpis = [
    { label: "Total Students", value: stats?.total_students ?? "—", icon: Users, color: "bg-sky text-white", testid: "stat-students" },
    { label: "Exercises", value: stats?.total_exercises ?? "—", icon: BookOpen, color: "bg-watermelon text-white", testid: "stat-exercises" },
    { label: "Attempts", value: stats?.total_attempts ?? "—", icon: ListChecks, color: "bg-lemon text-ink", testid: "stat-attempts" },
    { label: "Avg. Accuracy", value: stats ? `${stats.avg_accuracy}%` : "—", icon: Target, color: "bg-leaf text-white", testid: "stat-accuracy" },
  ];

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Admin dashboard</h1>
          <p className="text-lg text-stone mt-2">Overview of student practice on MathWhiz Beads.</p>
        </div>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} data-testid={k.testid} className="p-6 bg-white rounded-3xl card-tactile">
                <div className={`w-11 h-11 rounded-2xl ${k.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-pebble">{k.label}</div>
                <div className="font-heading text-4xl font-bold text-ink mt-1">{loading ? "…" : k.value}</div>
              </div>
            );
          })}
        </section>

        <section>
          <h2 className="font-heading text-2xl font-bold text-ink mb-4">Recent attempts</h2>
          <div className="bg-white rounded-3xl card-tactile overflow-hidden">
            {loading ? (
              <div className="p-8 text-stone">Loading…</div>
            ) : (stats?.recent_attempts?.length ?? 0) === 0 ? (
              <div className="p-8 text-center text-stone">No completed attempts yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-canvas">
                    <tr className="text-left text-xs uppercase tracking-wider text-stone">
                      <th className="p-4 font-bold">Student</th>
                      <th className="p-4 font-bold">Exercise</th>
                      <th className="p-4 font-bold">Date</th>
                      <th className="p-4 font-bold">Score</th>
                      <th className="p-4 font-bold">Accuracy</th>
                      <th className="p-4 font-bold">Time</th>
                      <th className="p-4 font-bold"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="recent-attempts-table" className="divide-y divide-cloud">
                    {stats.recent_attempts.map((a) => (
                      <tr key={a.id} className="hover:bg-canvas/50">
                        <td className="p-4 font-semibold text-ink">{a.student_name}</td>
                        <td className="p-4 text-stone">{a.exercise_title}</td>
                        <td className="p-4 text-stone">{fmtDate(a.submitted_at)}</td>
                        <td className="p-4 font-heading font-bold text-ink">{a.score}/{a.total_questions}</td>
                        <td className="p-4 font-semibold text-leaf">{a.accuracy}%</td>
                        <td className="p-4 text-stone">{fmtTime(a.time_taken_seconds)}</td>
                        <td className="p-4">
                          <Link data-testid={`view-attempt-${a.id}`} to={`/admin/attempts/${a.id}`} className="text-sky font-bold hover:underline">View</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
